import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { STAGE_DEFINITIONS } from '@tailoring-crm/shared';
import { ReportChart } from '../components/reports/report-chart.js';
import type { RangePickerValue } from '../components/reports/range-picker.js';
import { ReportShell, StatTile } from '../components/reports/report-shell.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { useHasModule } from '../lib/billing/entitlements.js';
import { formatNumber, formatPercent } from '../lib/format.js';
import { productionReportCsvPath } from '../lib/reports/reports-api.js';
import { useProductionReport } from '../lib/reports/use-reports.js';

/**
 * How the workshop is running: what came in, what went out, whether it went out on time, how
 * long each stage takes, and where the pile is right now.
 *
 * The stage table shows the median beside the average deliberately. One lehenga that sat through
 * a wedding season drags a quarter's average; the median says what a normal garment does, and the
 * gap between the two is itself the signal that something unusual happened.
 *
 * "Work in hand" is not bounded by the chosen period and says so. Every other figure here is
 * history; that one is the table as it stands while you read it, which is what somebody deciding
 * what to hand out this afternoon actually needs.
 */
const DAY_MS = 86_400_000;

function days(ms: number): string {
  return (ms / DAY_MS).toFixed(1);
}

export function ProductionReportPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [range, setRange] = useState<RangePickerValue>({ preset: 'last_30_days' });

  const allowed = role !== 'tailor';
  const inPlan = useHasModule('advanced_analytics');
  const report = useProductionReport(range, { enabled: allowed && inPlan });
  const data = report.data;

  return (
    <ReportShell
      title={t('reports.production.title')}
      description={t('reports.production.description')}
      range={range}
      onRangeChange={setRange}
      resolved={data?.range}
      csvPath={productionReportCsvPath(range)}
      isPending={report.isPending && allowed && inPlan}
      allowed={allowed}
      module="advanced_analytics"
    >
      {data ? (
        <>
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile
              label={t('reports.production.booked')}
              value={formatNumber(data.throughput.itemsBooked)}
            />
            <StatTile
              label={t('reports.production.delivered')}
              value={formatNumber(data.throughput.itemsDelivered)}
              hint={t('reports.production.perDay', { count: data.throughput.itemsPerDay })}
            />
            <StatTile
              label={t('reports.production.onTimeRate')}
              value={formatPercent(data.delivery.onTimeRate, 0)}
              hint={t('reports.production.lateCount', { count: data.delivery.late })}
            />
            <StatTile
              label={t('reports.production.averageLate')}
              value={t('reports.production.dayCount', { count: data.delivery.averageLateDays })}
              {...(data.delivery.unpromised > 0
                ? { hint: t('reports.production.unpromised', { count: data.delivery.unpromised }) }
                : {})}
            />
          </dl>

          <ReportChart
            title={t('reports.production.byDay')}
            labelHeading={t('reports.day')}
            valueHeading={t('reports.production.delivered')}
            points={data.byDay.map((row) => ({
              label: row.day,
              value: row.delivered,
              display: formatNumber(row.delivered),
            }))}
          />

          <section className="flex flex-col gap-2" aria-label={t('reports.production.stageTimes')}>
            <h2 className="text-sm font-semibold">{t('reports.production.stageTimes')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th scope="col" className="py-1 font-medium">
                      {t('reports.production.stage')}
                    </th>
                    <th scope="col" className="py-1 text-right font-medium">
                      {t('reports.production.moves')}
                    </th>
                    <th scope="col" className="py-1 text-right font-medium">
                      {t('reports.production.median')}
                    </th>
                    <th scope="col" className="py-1 text-right font-medium">
                      {t('reports.production.average')}
                    </th>
                    <th scope="col" className="py-1 text-right font-medium">
                      {t('reports.production.longest')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.stageDurations.map((row) => (
                    <tr key={row.stage} className="border-t border-border">
                      <th scope="row" className="py-1.5 text-left font-normal">
                        {t(`stage.${row.stage}`, {
                          defaultValue: STAGE_DEFINITIONS[row.stage].defaultLabel,
                        })}
                      </th>
                      <td className="py-1.5 text-right tabular-nums">{formatNumber(row.moves)}</td>
                      <td className="py-1.5 text-right tabular-nums">{days(row.medianMs)}</td>
                      <td className="py-1.5 text-right tabular-nums">{days(row.averageMs)}</td>
                      <td className="py-1.5 text-right tabular-nums">{days(row.longestMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <ReportChart
            title={t('reports.production.workInHand')}
            labelHeading={t('reports.production.stage')}
            valueHeading={t('reports.production.items')}
            initialView="table"
            points={data.workInProgress.map((row) => ({
              label: t(`stage.${row.stage}`, {
                defaultValue: STAGE_DEFINITIONS[row.stage].defaultLabel,
              }),
              value: row.items,
              display:
                row.overdue > 0
                  ? `${formatNumber(row.items)} · ${t('reports.production.overdueCount', {
                      count: row.overdue,
                    })}`
                  : formatNumber(row.items),
            }))}
          />
          <p className="text-xs text-muted-foreground">{t('reports.production.workInHandNote')}</p>
        </>
      ) : null}
    </ReportShell>
  );
}

export default ProductionReportPage;
